import { updateApplicationVariable } from "../api/controllers/applicationVariables";
import { updateOrganizationVariable } from "../api/controllers/organizationVariables";
import ApplicationVariable from "../models/applications/ApplicationVariable";
import OrganizationVariable from "../models/organizations/OrganizationVariable";
import IResult from "../models/result/IResult";

export type ReferenceKind = "variable" | "prompt" | "constant";
export type ReferenceScope = "Application" | "Organization";

export interface ResolvedReferenceVariable {
    id?: string;
    name: string;
    value: string;
    scope?: ReferenceScope;
    applicationId?: string;
    organizationId?: string;
    isFound: boolean;
}

export interface VariableReference extends ResolvedReferenceVariable {
    start: number;
    end: number;
    text: string;
    kind: ReferenceKind;
}

export const REFERENCE_VARIABLE_UPDATED_EVENT = "voicify-reference-variable-updated";
export const REFERENCE_VARIABLE_PATTERN = /\{\{(variable|prompt|constant)\.([A-Za-z0-9_-]+)\}\}/g;

type ConditionalComparisonOperator = "==" | "!=";
type ConditionalComparisonValue =
    | { type: "string"; value: string }
    | { type: "number"; value: number }
    | { type: "boolean"; value: boolean }
    | { type: "null"; value: null };

interface ParsedConditionalVariableExpression {
    kind: ReferenceKind;
    name: string;
    operator: ConditionalComparisonOperator;
    comparison: ConditionalComparisonValue;
    whenTrue: string;
    whenFalse: string;
}

interface TemplateExpressionRange {
    start: number;
    end: number;
    text: string;
    body: string;
}

const CONDITIONAL_VARIABLE_EXPRESSION_PATTERN = /^\s*(variable|prompt|constant)\.([A-Za-z0-9_-]+)\s*(==|!=)\s*("(?:\\[nrt\\'"]|[^"\\])*"|'(?:\\[nrt\\'"]|[^'\\])*'|-?(?:\d+(?:\.\d+)?|\.\d+)|true|false|null)\s*\?\s*("(?:\\[nrt\\'"]|[^"\\])*"|'(?:\\[nrt\\'"]|[^'\\])*')\s*:\s*("(?:\\[nrt\\'"]|[^"\\])*"|'(?:\\[nrt\\'"]|[^'\\])*')\s*$/;

const decodeQuotedExpressionValue = (value: string): string => {
    let decoded = "";

    for (let index = 1; index < value.length - 1; index += 1) {
        const character = value[index];
        if (character !== "\\" || index + 1 >= value.length - 1) {
            decoded += character;
            continue;
        }

        const escapedCharacter = value[index + 1];
        switch (escapedCharacter) {
            case "n":
                decoded += "\n";
                break;
            case "r":
                decoded += "\r";
                break;
            case "t":
                decoded += "\t";
                break;
            case "\\":
            case "\"":
            case "'":
                decoded += escapedCharacter;
                break;
            default:
                decoded += `\\${escapedCharacter}`;
                break;
        }
        index += 1;
    }

    return decoded;
};

const parseConditionalComparisonValue = (value: string): ConditionalComparisonValue | null => {
    if (value.startsWith("\"") || value.startsWith("'")) {
        return { type: "string", value: decodeQuotedExpressionValue(value) };
    }
    if (value === "true" || value === "false") {
        return { type: "boolean", value: value === "true" };
    }
    if (value === "null") {
        return { type: "null", value: null };
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue)
        ? { type: "number", value: numericValue }
        : null;
};

const parseConditionalVariableExpression = (body: string): ParsedConditionalVariableExpression | null => {
    const match = CONDITIONAL_VARIABLE_EXPRESSION_PATTERN.exec(body);
    if (!match) {
        return null;
    }

    const comparison = parseConditionalComparisonValue(match[4]);
    if (!comparison) {
        return null;
    }

    return {
        kind: match[1] as ReferenceKind,
        name: match[2],
        operator: match[3] as ConditionalComparisonOperator,
        comparison,
        whenTrue: decodeQuotedExpressionValue(match[5]),
        whenFalse: decodeQuotedExpressionValue(match[6])
    };
};

const isNestedTemplateReferenceStart = (text: string, start: number): boolean => (
    /^\{\{(?:variable|prompt|constant)\./.test(text.substring(start))
);

/** Finds complete outer {{...}} ranges without stopping at a nested reference in a quoted branch. */
const findTemplateExpressionRanges = (text: string): TemplateExpressionRange[] => {
    const ranges: TemplateExpressionRange[] = [];

    for (let start = 0; start < text.length - 1; start += 1) {
        if (text.substring(start, start + 2) !== "{{") {
            continue;
        }

        let cursor = start + 2;
        let depth = 1;
        let quote: "\"" | "'" | null = null;
        let escaped = false;

        while (cursor < text.length - 1) {
            const character = text[cursor];
            const pair = text.substring(cursor, cursor + 2);

            if (quote) {
                if (escaped) {
                    escaped = false;
                    cursor += 1;
                    continue;
                }
                if (character === "\\") {
                    escaped = true;
                    cursor += 1;
                    continue;
                }
                if (pair === "{{" && isNestedTemplateReferenceStart(text, cursor)) {
                    depth += 1;
                    cursor += 2;
                    continue;
                }
                if (pair === "}}" && depth > 1) {
                    depth -= 1;
                    cursor += 2;
                    continue;
                }
                if (character === quote) {
                    quote = null;
                }
                cursor += 1;
                continue;
            }

            if (character === "\"" || character === "'") {
                quote = character;
                cursor += 1;
                continue;
            }
            if (pair === "{{") {
                depth += 1;
                cursor += 2;
                continue;
            }
            if (pair === "}}") {
                depth -= 1;
                cursor += 2;
                if (depth === 0) {
                    const expressionText = text.substring(start, cursor);
                    ranges.push({
                        start,
                        end: cursor,
                        text: expressionText,
                        body: expressionText.substring(2, expressionText.length - 2)
                    });
                    start = cursor - 1;
                    break;
                }
                continue;
            }

            cursor += 1;
        }
    }

    return ranges;
};

export const getResolvedReferenceVariablesByName = (
    applicationVariables: ApplicationVariable[],
    organizationVariables: OrganizationVariable[]
): Record<string, ResolvedReferenceVariable> => {
    const variables: Record<string, ResolvedReferenceVariable> = {};

    organizationVariables.forEach(variable => {
        variables[variable.name] = {
            id: variable.id,
            name: variable.name,
            value: variable.value || "",
            scope: "Organization",
            organizationId: variable.organizationId,
            isFound: true
        };
    });

    applicationVariables.forEach(variable => {
        variables[variable.name] = {
            id: variable.id,
            name: variable.name,
            value: variable.value || "",
            scope: "Application",
            applicationId: variable.applicationId,
            isFound: true
        };
    });

    return variables;
};

export const findVariableReferences = (
    text: string,
    variablesByName: Record<string, ResolvedReferenceVariable>
): VariableReference[] => {
    const references: VariableReference[] = [];
    const regex = new RegExp(REFERENCE_VARIABLE_PATTERN.source, "g");
    let match: RegExpExecArray;

    while ((match = regex.exec(text)) !== null) {
        const variable = variablesByName[match[2]];
        references.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0],
            kind: match[1] as ReferenceKind,
            name: match[2],
            value: variable?.value || "",
            id: variable?.id,
            scope: variable?.scope,
            applicationId: variable?.applicationId,
            organizationId: variable?.organizationId,
            isFound: !!variable?.isFound
        });
    }

    return references;
};

const getUsableAssociatedVariableEntries = (
    valuesByAssociatedVariable: Record<string, string> = {}
): Array<[string, string]> => {
    return Object.keys(valuesByAssociatedVariable)
        .map(key => [key, `${valuesByAssociatedVariable[key] ?? ""}`.trim()] as [string, string])
        .filter(([key, value]) => !!key && !!value && value !== "-1" && value !== key)
        .sort(([left], [right]) => right.length - left.length);
};

/**
 * Finds literal associated-variable tokens so display-only consumers can show
 * their values without replacing the raw tokens that must be submitted.
 */
export const findAssociatedVariableReferences = (
    text: string,
    valuesByAssociatedVariable: Record<string, string> = {},
    excludedRanges: Array<Pick<VariableReference, "start" | "end">> = []
): VariableReference[] => {
    const references: VariableReference[] = [];

    getUsableAssociatedVariableEntries(valuesByAssociatedVariable)
        // Direct inline replacement is limited to token-shaped variables. Bare
        // identifiers such as DISABLED_DAYS are only meaningful as whole field
        // values and should not replace matching words inside prose.
        .filter(([token]) => token.startsWith("{") && token.endsWith("}"))
        .forEach(([token, value]) => {
            let start = text.indexOf(token);
            while (start >= 0) {
                const end = start + token.length;
                const overlapsExistingReference = [...excludedRanges, ...references]
                    .some(reference => start < reference.end && end > reference.start);

                if (!overlapsExistingReference) {
                    references.push({
                        start,
                        end,
                        text: token,
                        kind: "variable",
                        name: token,
                        value,
                        isFound: true
                    });
                }

                start = text.indexOf(token, end);
            }
        });

    return references.sort((left, right) => left.start - right.start);
};

export const resolveReferenceVariables = (
    text: string,
    variablesByName: Record<string, ResolvedReferenceVariable>,
    maxDepth: number = 10
): string => {
    if (maxDepth <= 0) {
        return text;
    }

    let replacedReference = false;
    const regex = new RegExp(REFERENCE_VARIABLE_PATTERN.source, "g");
    const resolved = text.replace(regex, (match, _kind: ReferenceKind, name: string) => {
        const variable = variablesByName[name];
        if (!variable?.isFound) {
            return match;
        }

        replacedReference = true;
        return variable.value || "";
    });

    return replacedReference && resolved !== text
        ? resolveReferenceVariables(resolved, variablesByName, maxDepth - 1)
        : resolved;
};

export const resolveAssociatedVariableValues = (
    text: string,
    valuesByAssociatedVariable: Record<string, string> = {},
    maxDepth: number = 10
): string => {
    const entries = getUsableAssociatedVariableEntries(valuesByAssociatedVariable);
    let resolved = text;
    const seenValues = new Set<string>([resolved]);

    for (let depth = 0; depth < maxDepth; depth += 1) {
        let next = resolved;

        entries.forEach(([token, value]) => {
            if (token.startsWith("{") && token.endsWith("}")) {
                next = next.split(token).join(value);
            } else if (next.trim() === token) {
                next = next.replace(token, value);
            }
        });

        if (next === resolved || seenValues.has(next)) {
            return next;
        }

        seenValues.add(next);
        resolved = next;
    }

    return resolved;
};

const getExactAssociatedExpressionValue = (
    kind: ReferenceKind,
    name: string,
    valuesByAssociatedVariable: Record<string, string>
): { isFound: boolean; value: string } => {
    const candidateKeys = [
        `{{${kind}.${name}}}`,
        `{{variable.${name}}}`,
        `{${name}}`
    ];

    for (const key of candidateKeys) {
        if (Object.prototype.hasOwnProperty.call(valuesByAssociatedVariable, key)) {
            const value = `${valuesByAssociatedVariable[key] ?? ""}`;
            if (value.trim()) {
                return { isFound: true, value };
            }
        }
    }

    return { isFound: false, value: "" };
};

const resolveReferenceVariablesForDisplay = (
    text: string,
    variablesByName: Record<string, ResolvedReferenceVariable>,
    valuesByAssociatedVariable: Record<string, string>,
    maxDepth: number = 10
): string => {
    if (maxDepth <= 0) {
        return text;
    }

    let replacedReference = false;
    const regex = new RegExp(REFERENCE_VARIABLE_PATTERN.source, "g");
    const resolved = text.replace(regex, (match, kind: ReferenceKind, name: string) => {
        const associatedValue = getExactAssociatedExpressionValue(
            kind,
            name,
            valuesByAssociatedVariable
        );
        if (associatedValue.isFound) {
            replacedReference = true;
            return associatedValue.value;
        }

        const variable = variablesByName[name];
        if (!variable?.isFound) {
            return match;
        }

        replacedReference = true;
        return variable.value || "";
    });

    return replacedReference && resolved !== text
        ? resolveReferenceVariablesForDisplay(
            resolved,
            variablesByName,
            valuesByAssociatedVariable,
            maxDepth - 1
        )
        : resolved;
};

const getConditionalExpressionOperand = (
    kind: ReferenceKind,
    name: string,
    variablesByName: Record<string, ResolvedReferenceVariable>,
    valuesByAssociatedVariable: Record<string, string>
): { isFound: boolean; value: string } => {
    const exactAssociatedValue = getExactAssociatedExpressionValue(
        kind,
        name,
        valuesByAssociatedVariable
    );
    const variable = variablesByName[name];

    if (exactAssociatedValue.isFound) {
        return exactAssociatedValue;
    }

    if (!variable?.isFound) {
        return { isFound: false, value: "" };
    }

    const resolvedVariableValue = resolveAssociatedVariableValues(
        resolveReferenceVariablesForDisplay(
            variable.value || "",
            variablesByName,
            valuesByAssociatedVariable
        ),
        valuesByAssociatedVariable
    );
    const trimmedValue = resolvedVariableValue.trim();
    const unresolvedSelfReferences = [
        `{${name}}`,
        `{{${kind}.${name}}}`,
        `{{variable.${name}}}`,
        `{{prompt.${name}}}`,
        `{{constant.${name}}}`
    ];

    if (!trimmedValue) {
        return { isFound: false, value: resolvedVariableValue };
    }

    const isUnresolvedReference = unresolvedSelfReferences.includes(trimmedValue)
        || /\{\{(?:variable|prompt|constant)\.[A-Za-z0-9_-]+\}\}/.test(trimmedValue)
        || /\{[A-Za-z0-9_-]+\}/.test(trimmedValue);

    return isUnresolvedReference
        ? { isFound: false, value: resolvedVariableValue }
        : { isFound: true, value: resolvedVariableValue };
};

const conditionalValuesAreEqual = (
    actualValue: string,
    comparison: ConditionalComparisonValue
): boolean => {
    switch (comparison.type) {
        case "number": {
            const trimmedValue = actualValue.trim();
            if (!trimmedValue) {
                return false;
            }
            const numericValue = Number(trimmedValue);
            return Number.isFinite(numericValue) && numericValue === comparison.value;
        }
        case "boolean":
            return actualValue.trim() === `${comparison.value}`;
        case "null":
            return actualValue.trim() === "null";
        case "string":
        default:
            return actualValue === comparison.value;
    }
};

/**
 * Finds the restricted equality ternaries used by response variables. These are
 * display references only; the full source expression remains the saved value.
 */
export const findConditionalVariableReferences = (
    text: string,
    variablesByName: Record<string, ResolvedReferenceVariable>,
    valuesByAssociatedVariable: Record<string, string> = {}
): VariableReference[] => {
    return findTemplateExpressionRanges(text).reduce((references, range) => {
        const expression = parseConditionalVariableExpression(range.body);
        if (!expression) {
            const conditionalIdentity = /^\s*(variable|prompt|constant)\.([A-Za-z0-9_-]+)[\s\S]*\?[\s\S]*:[\s\S]*$/.exec(range.body);
            if (conditionalIdentity) {
                const variable = variablesByName[conditionalIdentity[2]];
                references.push({
                    start: range.start,
                    end: range.end,
                    text: range.text,
                    kind: conditionalIdentity[1] as ReferenceKind,
                    name: conditionalIdentity[2],
                    value: range.text,
                    id: variable?.id,
                    scope: variable?.scope,
                    applicationId: variable?.applicationId,
                    organizationId: variable?.organizationId,
                    isFound: false
                });
            }
            return references;
        }

        const operand = getConditionalExpressionOperand(
            expression.kind,
            expression.name,
            variablesByName,
            valuesByAssociatedVariable
        );
        const variable = variablesByName[expression.name];
        const isEqual = operand.isFound
            ? conditionalValuesAreEqual(operand.value, expression.comparison)
            : false;
        const usesTrueBranch = expression.operator === "=="
            ? isEqual
            : !isEqual;

        references.push({
            start: range.start,
            end: range.end,
            text: range.text,
            kind: expression.kind,
            name: expression.name,
            value: operand.isFound
                ? (usesTrueBranch ? expression.whenTrue : expression.whenFalse)
                : range.text,
            id: variable?.id,
            scope: variable?.scope,
            applicationId: variable?.applicationId,
            organizationId: variable?.organizationId,
            isFound: operand.isFound
        });

        return references;
    }, [] as VariableReference[]);
};

const resolveReferenceDisplayIteration = (
    text: string,
    variablesByName: Record<string, ResolvedReferenceVariable>,
    valuesByAssociatedVariable: Record<string, string>,
    maxDepth: number
): string => {
    const resolvePlainText = (plainText: string): string => resolveAssociatedVariableValues(
        resolveReferenceVariablesForDisplay(
            plainText,
            variablesByName,
            valuesByAssociatedVariable,
            maxDepth
        ),
        valuesByAssociatedVariable,
        maxDepth
    );
    const conditionalReferences = findConditionalVariableReferences(
        text,
        variablesByName,
        valuesByAssociatedVariable
    );

    if (conditionalReferences.length === 0) {
        return resolvePlainText(text);
    }

    let resolved = "";
    let cursor = 0;
    conditionalReferences.forEach(reference => {
        resolved += resolvePlainText(text.substring(cursor, reference.start));
        resolved += reference.isFound
            ? resolvePlainText(reference.value)
            : reference.text;
        cursor = reference.end;
    });
    resolved += resolvePlainText(text.substring(cursor));
    return resolved;
};

/** Resolves nested constants first, then any module associated-variable tokens. */
export const resolveReferenceDisplayValue = (
    text: string,
    variablesByName: Record<string, ResolvedReferenceVariable>,
    valuesByAssociatedVariable: Record<string, string> = {},
    maxDepth: number = 10
): string => {
    let resolved = text;
    const seenValues = new Set<string>([resolved]);

    for (let depth = 0; depth < maxDepth; depth += 1) {
        const next = resolveReferenceDisplayIteration(
            resolved,
            variablesByName,
            valuesByAssociatedVariable,
            maxDepth
        );

        if (next === resolved || seenValues.has(next)) {
            return next;
        }

        seenValues.add(next);
        resolved = next;
    }

    return resolved;
};

export const unwrapVariableReference = (
    text: string,
    reference: Pick<VariableReference, "start" | "end" | "text" | "value">
): string => {
    if (text.substring(reference.start, reference.end) !== reference.text) {
        return text;
    }

    return `${text.substring(0, reference.start)}${reference.value}${text.substring(reference.end)}`;
};

const invalidResult = (message: string): IResult<ResolvedReferenceVariable> => ({
    resultType: "Invalid",
    data: null,
    errors: [message]
});

export const updateResolvedReferenceVariable = async (
    variable: ResolvedReferenceVariable,
    value: string
): Promise<IResult<ResolvedReferenceVariable>> => {
    if (!variable.id || !variable.scope) {
        return invalidResult("This reference is missing the information required to update it");
    }

    if (variable.scope === "Application") {
        if (!variable.applicationId) {
            return invalidResult("This reference is missing its application");
        }

        const result = await updateApplicationVariable(variable.id, {
            id: variable.id,
            name: variable.name,
            value,
            applicationId: variable.applicationId
        });
        return result.resultType === "Ok"
            ? {
                  resultType: "Ok",
                  data: {
                      ...variable,
                      value: result.data?.value === undefined ? value : result.data.value
                  },
                  errors: []
              }
            : invalidResult(result.errors?.[0] || "Could not update the application reference");
    }

    if (!variable.organizationId) {
        return invalidResult("This reference is missing its organization");
    }

    const result = await updateOrganizationVariable(variable.id, {
        id: variable.id,
        name: variable.name,
        value,
        organizationId: variable.organizationId
    });
    return result.resultType === "Ok"
        ? {
              resultType: "Ok",
              data: {
                  ...variable,
                  value: result.data?.value === undefined ? value : result.data.value
              },
              errors: []
          }
        : invalidResult(result.errors?.[0] || "Could not update the organization reference");
};

export const notifyReferenceVariableUpdated = (variable: ResolvedReferenceVariable): void => {
    if (typeof window === "undefined") {
        return;
    }

    window.dispatchEvent(new CustomEvent<ResolvedReferenceVariable>(REFERENCE_VARIABLE_UPDATED_EVENT, {
        detail: variable
    }));
};
